import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, Badge, Button, Modal, message, DatePicker, Select, Space, Card, Typography, Row, Col, List, Tag, Input, Tooltip, Radio } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBan, faCheck, faTrash, faHeart, faUser, faMapMarkerAlt } from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';
import { COLORS } from '../../constants/colors';
import styled from 'styled-components';
import BlockedDateService from '../../services/BlockedDateService';
import BookingService from '../../services/BookingService';
import {
  TabContentWrapper,
  AnimatedCalendarSection,
  AnimatedSidePanel
} from './NewStyled';

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Text } = Typography;

const CalendarContainer = styled.div`
  .ant-picker-calendar {
    background: white;
    border-radius: 12px;
    padding: 24px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    
    @media (max-width: 768px) {
      padding: 12px;
    }
  }

  .ant-picker-calendar-header {
    padding: 16px 0;
    border-bottom: 2px solid ${COLORS.border};
    
    @media (max-width: 768px) {
      padding: 12px 0;
      flex-direction: column !important;
      align-items: stretch !important;
      gap: 12px;
      
      > div {
        display: flex !important;
        flex-direction: column !important;
        gap: 12px;
        width: 100% !important;
      }
    }
    
    /* Make dropdowns and buttons larger */
    .ant-select {
      min-width: 120px;
      
      @media (max-width: 768px) {
        min-width: 0;
        flex: 1;
        width: 100% !important;
      }
      
      .ant-select-selector {
        height: 40px !important;
        padding: 4px 11px !important;
        font-size: 16px !important;
        border-radius: 8px !important;
        display: flex !important;
        align-items: center !important;
        
        @media (max-width: 768px) {
          height: 44px !important;
          font-size: 16px !important;
          padding: 4px 12px !important;
        }
      }
      
      .ant-select-selection-item {
        line-height: 32px !important;
        display: flex !important;
        align-items: center !important;
        
        @media (max-width: 768px) {
          line-height: 36px !important;
        }
      }
      
      .ant-select-arrow {
        top: 50% !important;
        transform: translateY(50%) !important;
        inset-inline-end: 11px !important;
      }
    }
    
    .ant-radio-group {
      display: inline-flex !important;
      
      @media (max-width: 768px) {
        width: 100%;
        justify-content: center;
        order: -1;
      }
      
      .ant-radio-button-wrapper {
        height: 40px !important;
        line-height: 38px !important;
        font-size: 16px !important;
        padding: 0 20px !important;
        
        @media (max-width: 768px) {
          height: 44px !important;
          line-height: 42px !important;
          font-size: 16px !important;
          padding: 0 20px !important;
          flex: 1;
          text-align: center;
        }
        
        &:first-child {
          border-radius: 8px 0 0 8px !important;
        }
        
        &:last-child {
          border-radius: 0 8px 8px 0 !important;
        }
        
        &:not(:first-child):not(:last-child) {
          border-radius: 0 !important;
        }
      }
    }
  }
  
  /* Mobile: Put dropdowns side by side in a row */
  @media (max-width: 768px) {
    .ant-picker-calendar-header > div > div:has(.ant-select) {
      display: flex !important;
      flex-direction: row !important;
      gap: 8px;
      width: 100%;
    }
    
    .ant-picker-calendar-header .ant-select {
      flex: 1 !important;
    }
  }

  /* Make calendar larger */
  .ant-picker-content {
    th {
      font-size: 15px !important;
      padding: 12px 0 !important;
      text-align: center !important;
    }
  }

  .ant-picker-cell {
    padding: 8px 0 !important;
    text-align: center !important;
    
    .ant-picker-cell-inner {
      width: 40px !important;
      height: 40px !important;
      font-size: 15px !important;
      border-radius: 50% !important;
      display: inline-flex !important;
      align-items: center !important;
      justify-content: center !important;
      margin: 0 auto !important;
    }
  }

  /* Today's date - black circle outline, remove ALL default backgrounds/borders */
  .ant-picker-cell-in-view.ant-picker-cell-today .ant-picker-cell-inner {
    background: white !important;
    border: 2px solid #000 !important;
    color: #000 !important;
    font-weight: 600 !important;
    box-shadow: none !important;
    outline: none !important;
  }
  
  .ant-picker-cell-in-view.ant-picker-cell-today .ant-picker-cell-inner:hover {
    background: white !important;
    border: 2px solid #000 !important;
  }
  
  /* Remove the blue background/border that Ant Design adds to today's cell */
  .ant-picker-cell-in-view.ant-picker-cell-today::before {
    border: none !important;
    border-color: transparent !important;
    background: transparent !important;
    content: none !important;
  }
  
  .ant-picker-cell-in-view.ant-picker-cell-today::after {
    display: none !important;
  }
  
  /* Target the specific selector that Ant Design uses */
  .ant-picker-calendar .ant-picker-cell-in-view.ant-picker-cell-today .ant-picker-cell-inner::before {
    border: none !important;
    display: none !important;
  }
  
  .ant-picker-cell-in-view.ant-picker-cell-today {
    background: transparent !important;
    border: none !important;
    box-shadow: none !important;
    outline: none !important;
  }
  
  /* Override any panel selection styling */
  .ant-picker-panel .ant-picker-cell-in-view.ant-picker-cell-today {
    background: transparent !important;
  }
  
  .ant-picker-panel .ant-picker-cell-in-view.ant-picker-cell-today .ant-picker-cell-inner {
    background: white !important;
    border: 2px solid #000 !important;
  }

  /* Selected date with outlined circle */
  .ant-picker-cell-in-view.ant-picker-cell-selected .ant-picker-cell-inner {
    background: white !important;
    color: ${COLORS.$primaryBlue} !important;
    border: 2px solid ${COLORS.$primaryBlue} !important;
    font-weight: 600;
  }
  
  /* Today's date when selected */
  .ant-picker-cell-in-view.ant-picker-cell-today.ant-picker-cell-selected .ant-picker-cell-inner {
    background: white !important;
    color: ${COLORS.$primaryBlue} !important;
    border: 2px solid ${COLORS.$primaryBlue} !important;
  }

  /* Blocked date styling - make cell position relative for absolute children */
  .ant-picker-cell {
    position: relative !important;
  }
  
  .ant-picker-cell-inner {
    position: relative !important;
    z-index: 1 !important;
  }
`;

const BlockedDatesList = styled.div`
  max-height: 500px;
  overflow-y: auto;
  
  .ant-list-item {
    flex-wrap: wrap;
    
    .ant-list-item-meta {
      flex: 1;
      min-width: 0;
      overflow: hidden;
    }
    
    .ant-list-item-action {
      margin-left: 8px;
      flex-shrink: 0;
    }
  }
  
  .ant-tag {
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  
  @media (max-width: 768px) {
    .ant-list-item-meta-title {
      max-width: calc(100% - 40px);
    }
  }
`;

const AvailabilityManager = () => {
  const [blockedDates, setBlockedDates] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedDates, setSelectedDates] = useState(null);
  const [blockType, setBlockType] = useState('full-day');
  const [reason, setReason] = useState('');
  const [calendarKey, setCalendarKey] = useState(0);
  const [bookingsByDate, setBookingsByDate] = useState({});

  useEffect(() => {
    loadBlockedDates();
    loadBookings();
  }, []);

  const loadBlockedDates = async () => {
    try {
      const response = await BlockedDateService.getBlockedDates({ status: 'active' });
      
      if (response.success) {
        const formattedDates = response.data.map(block => ({
          id: block._id,
          startDate: block.startDate,
          endDate: block.endDate,
          type: block.type,
          reason: block.reason,
          appliesTo: block.appliesTo,
          eventTypes: block.eventTypes || []
        }));
        setBlockedDates(formattedDates);
        // Force calendar to re-render
        setCalendarKey(prev => {
          console.log('🔑 Calendar key changing from', prev, 'to', prev + 1);
          return prev + 1;
        });
      } else {
        // If backend is not running or returns error, set empty array
        console.log('❌ Backend response not successful:', response);
        setBlockedDates([]);
      }
    } catch (error) {
      console.error('💥 Error loading blocked dates:', error);
      // Set empty array on error so UI still works
      setBlockedDates([]);
    }
  };

  const loadBookings = async () => {
    try {
      const startDate = moment().startOf('month').subtract(1, 'month').format('YYYY-MM-DD');
      const endDate = moment().endOf('month').add(2, 'months').format('YYYY-MM-DD');
      
      const response = await BookingService.getAllBookings({
        startDate,
        endDate,
        status: 'confirmed'
      });
      
      if (response.success && response.data) {
        const bookings = Array.isArray(response.data) ? response.data : 
                        (response.data.bookings || response.data.data || []);
        
        // Group bookings by date (use UTC to match calendar dates)
        const grouped = {};
        bookings.forEach(booking => {
          // Handle both formats: timeSlot.startTime or bookingDate
          const dateSource = booking.timeSlot?.startTime || booking.bookingDate;
          if (!dateSource) return; // Skip if no date available
          
          const dateKey = moment.utc(dateSource).format('YYYY-MM-DD');
          if (!grouped[dateKey]) {
            grouped[dateKey] = [];
          }
          grouped[dateKey].push(booking);
        });
        
        setBookingsByDate(grouped);
      }
    } catch (error) {
      console.error('Error loading bookings:', error);
      setBookingsByDate({});
    }
  };

  const handleBlockDates = async () => {
    if (!selectedDates || selectedDates.length !== 2) {
      message.warning('Please select a date range');
      return;
    }

    try {
      const data = {
        startDate: selectedDates[0].format('YYYY-MM-DD'),
        endDate: selectedDates[1].format('YYYY-MM-DD'),
        type: blockType,
        reason: reason || 'Not specified',
        appliesTo: 'all'
      };

      const response = await BlockedDateService.createBlockedDate(data);
      
      if (response.success) {
        message.success('Dates blocked successfully');
        loadBlockedDates(); // Reload the list
        loadBookings(); // Reload bookings
        setModalVisible(false);
        setSelectedDates(null);
        setReason('');
      } else {
        message.error(response.message || 'Failed to block dates');
      }
    } catch (error) {
      console.error('Error blocking dates:', error);
      message.error('Failed to block dates');
    }
  };

  const handleUnblockDate = async (blockId) => {
    try {
      const response = await BlockedDateService.deleteBlockedDate(blockId, false);
      
      if (response.success) {
        message.success('Date unblocked successfully');
        loadBlockedDates(); // Reload the list
        loadBookings(); // Reload bookings
      } else {
        message.error(response.message || 'Failed to unblock date');
      }
    } catch (error) {
      console.error('Error unblocking date:', error);
      message.error('Failed to unblock date');
    }
  };

  const isDateBlocked = (date) => {
    if (!blockedDates || blockedDates.length === 0) {
      return false;
    }
    
    // The date parameter is already a moment object from Ant Design Calendar
    // Format it directly without wrapping in moment() again
    const dateStr = date.format('YYYY-MM-DD');
    
    const result = blockedDates.some(block => {
      // Parse backend dates as UTC and format as YYYY-MM-DD
      const blockStartStr = moment.utc(block.startDate).format('YYYY-MM-DD');
      const blockEndStr = moment.utc(block.endDate).format('YYYY-MM-DD');
      
      // Compare date strings directly
      const isBlocked = dateStr >= blockStartStr && dateStr <= blockEndStr;
      
      return isBlocked;
    });
    
    return result;
  };

  const onPanelChange = () => {
    // Trigger re-render when month/year changes
  };

  const dateFullCellRender = useCallback((date) => {
    const isBlocked = isDateBlocked(date);
    const isCurrentMonth = date.month() === moment().month();
    const dateNum = date.date();
    const dateKey = date.format('YYYY-MM-DD');
    const dayBookings = bookingsByDate[dateKey] || [];
    const hasEvents = dayBookings.length > 0;
    
    const cellContent = (
      <div 
        className="ant-picker-cell-inner"
        title=""
        style={{
          position: 'relative',
          color: !isCurrentMonth ? '#00000040' : undefined,
          background: isBlocked ? '#fff1f0' : 'transparent',
          borderRadius: '50%',
          paddingRight: '1px'
        }}
      >
        {dateNum}
        {isBlocked && (
          <div style={{
            width: '4px',
            height: '4px',
            background: '#ff4d4f',
            borderRadius: '50%',
            position: 'absolute',
            bottom: '6px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 2
          }} />
        )}
        {hasEvents && !isBlocked && (
          <div style={{
            width: '5px',
            height: '5px',
            background: '#52c41a',
            borderRadius: '50%',
            position: 'absolute',
            bottom: '4px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 2
          }} />
        )}
      </div>
    );
    
    // If there are events, wrap in a tooltip
    if (hasEvents && !isBlocked) {
      const tooltipContent = (
        <div style={{ maxWidth: '300px' }}>
          <div style={{ fontWeight: 600, marginBottom: '8px', fontSize: '13px' }}>
            {date.format('MMMM D')} - {dayBookings.length} Event{dayBookings.length > 1 ? 's' : ''}
          </div>
          <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
            {dayBookings.map((booking, idx) => {
              // Handle both formats: timeSlot.startTime/endTime or bookingDate
              const startTime = booking.timeSlot?.startTime || booking.bookingDate;
              let endTime = booking.timeSlot?.endTime;
              
              // Fallback: If endTime doesn't exist (old bookings), calculate it from event type duration
              if (!endTime && startTime && booking.eventType?.duration) {
                endTime = moment.utc(startTime).add(booking.eventType.duration, 'minutes').toDate();
              }
              
              // Calculate duration for display
              let durationText = '';
              if (startTime && endTime) {
                const start = moment.utc(startTime);
                const end = moment.utc(endTime);
                const durationMinutes = end.diff(start, 'minutes');
                const hours = Math.floor(durationMinutes / 60);
                const minutes = durationMinutes % 60;
                
                if (hours > 0 && minutes > 0) {
                  durationText = ` (${hours}h ${minutes}m)`;
                } else if (hours > 0) {
                  durationText = ` (${hours}h)`;
                } else if (minutes > 0) {
                  durationText = ` (${minutes}m)`;
                }
              }
              
              return (
                <div key={idx} style={{ 
                  marginBottom: '6px', 
                  paddingBottom: '6px',
                  borderBottom: idx < dayBookings.length - 1 ? '1px solid rgba(255,255,255,0.2)' : 'none'
                }}>
                  <div style={{ fontSize: '13px', fontWeight: 500 }}>
                    {startTime ? moment.utc(startTime).format('h:mm A') : 'Time TBD'}
                    {endTime && ` - ${moment.utc(endTime).format('h:mm A')}`}
                    {durationText && <span style={{ fontSize: '12px', opacity: 0.9, marginLeft: '4px' }}>{durationText}</span>}
                  </div>
                  <div style={{ fontSize: '11px', marginBottom: '3px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <FontAwesomeIcon 
                      icon={faHeart} 
                      style={{ color: booking.eventType?.color || '#1890ff' }} 
                    />
                    <span>{booking.eventType?.name || 'Event'}</span>
                  </div>
                  <div style={{ fontSize: '11px', opacity: 0.85, display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '3px' }}>
                    <FontAwesomeIcon 
                      icon={faUser} 
                      style={{ color: booking.eventType?.color || '#1890ff' }} 
                    />
                    <span>{booking.userInfo?.firstName} {booking.userInfo?.lastName}</span>
                  </div>
                  {booking.userInfo?.customData?.businessEventName && (
                    <div style={{ fontSize: '11px', opacity: 0.85, fontStyle: 'italic', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <FontAwesomeIcon 
                        icon={faMapMarkerAlt} 
                        style={{ color: booking.eventType?.color || '#1890ff' }} 
                      />
                      <span>{booking.userInfo.customData.businessEventName}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      );
      
      return (
        <Tooltip title={tooltipContent} placement="right" overlayStyle={{ maxWidth: '350px' }}>
          {cellContent}
        </Tooltip>
      );
    }
    
    return cellContent;
  }, [blockedDates, bookingsByDate]);

  return (
    <TabContentWrapper>
      <Row gutter={24}>
        <Col xs={24} lg={16}>
          <AnimatedCalendarSection>
            <div style={{ marginBottom: 24 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <div style={{ fontSize: 18, fontWeight: 600 }}>
                  Availability Calendar
                </div>
                <Button
                  type="primary"
                  size="small"
                  icon={<FontAwesomeIcon icon={faBan} />}
                  onClick={() => setModalVisible(true)}
                  style={{
                    background: COLORS.$primaryBlue,
                    borderColor: COLORS.$primaryBlue,
                    fontSize: '13px',
                    height: '32px'
                  }}
                >
                  Block Dates
                </Button>
              </div>
              <CalendarContainer>
                <Calendar 
                  key={calendarKey}
                  fullscreen={false}
                  dateFullCellRender={dateFullCellRender}
                  onPanelChange={onPanelChange}
                headerRender={({ value, type, onChange, onTypeChange }) => {
                  const start = 0;
                  const end = 12;
                  const monthOptions = [];
                  
                  const localeData = value.localeData();
                  const months = [];
                  for (let i = 0; i < 12; i++) {
                    const monthMoment = value.clone().month(i);
                    months.push(localeData.monthsShort(monthMoment));
                  }
                  
                  for (let i = start; i < end; i++) {
                    monthOptions.push(
                      <Select.Option key={i} value={i}>
                        {months[i]}
                      </Select.Option>
                    );
                  }
                  
                  const year = value.year();
                  const month = value.month();
                  const options = [];
                  for (let i = year - 10; i < year + 10; i += 1) {
                    options.push(
                      <Select.Option key={i} value={i}>
                        {i}
                      </Select.Option>
                    );
                  }
                  
                  return (
                    <div className="calendar-header-custom" style={{ 
                      padding: '16px 0px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '12px',
                      borderBottom: '1px solid #f0f0f0'
                    }}>
                      <style>
                        {`
                          @media (min-width: 769px) {
                            .calendar-header-custom {
                              flex-direction: row !important;
                              justify-content: space-between !important;
                              align-items: center !important;
                            }
                          }
                          @media (max-width: 768px) {
                            .calendar-header-custom .calendar-header-toggle {
                              width: 100%;
                            }
                            .calendar-header-custom .calendar-header-toggle .ant-radio-group {
                              width: 100%;
                              display: flex !important;
                            }
                            .calendar-header-custom .calendar-header-toggle .ant-radio-button-wrapper {
                              flex: 1;
                              text-align: center;
                            }
                            .calendar-header-custom .calendar-header-selects {
                              width: 100%;
                            }
                            .calendar-header-custom .calendar-header-selects .ant-select {
                              flex: 1;
                            }
                          }
                        `}
                      </style>
                      <div className="calendar-header-toggle" style={{ display: 'flex', gap: '8px' }}>
                        <Radio.Group
                          size="large"
                          onChange={(e) => onTypeChange(e.target.value)}
                          value={type}
                          style={{ height: '44px', display: 'flex' }}
                        >
                          <Radio.Button value="month" style={{ height: '44px', lineHeight: '42px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>Month</Radio.Button>
                          <Radio.Button value="year" style={{ height: '44px', lineHeight: '42px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>Year</Radio.Button>
                        </Radio.Group>
                      </div>
                      
                      <div className="calendar-header-selects" style={{ display: 'flex', gap: '8px' }}>
                        <Select
                          size="large"
                          value={month}
                          onChange={(newMonth) => {
                            const now = value.clone().month(newMonth);
                            onChange(now);
                          }}
                          style={{ minWidth: 100, height: '44px', flex: 1 }}
                        >
                          {monthOptions}
                        </Select>
                        <Select
                          size="large"
                          value={year}
                          onChange={(newYear) => {
                            const now = value.clone().year(newYear);
                            onChange(now);
                          }}
                          style={{ minWidth: 100, height: '44px', flex: 1 }}
                        >
                          {options}
                        </Select>
                      </div>
                    </div>
                  );
                }}
              />
            </CalendarContainer>
            <div style={{ marginTop: 16, padding: 16, background: '#f5f5f5', borderRadius: 8 }}>
              <Space direction="vertical" size={8}>
                <Space>
                  <Badge status="error" />
                  <Text type="secondary">Blocked dates (not available for public booking)</Text>
                </Space>
                <Space>
                  <Badge status="success" />
                  <Text type="secondary">Dates with scheduled events (hover to view)</Text>
                </Space>
              </Space>
            </div>
          </div>
          </AnimatedCalendarSection>
        </Col>

        <Col xs={24} lg={8}>
          <AnimatedSidePanel>
            <Card 
              title={
                <Space>
                  <FontAwesomeIcon icon={faBan} />
                  <span>Blocked Dates</span>
                  <Tag color="red">{blockedDates.length}</Tag>
                </Space>
              }
            >
              <BlockedDatesList>
                {blockedDates.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: 40, color: COLORS.textMuted }}>
                    <FontAwesomeIcon icon={faCheck} size="2x" style={{ marginBottom: 16 }} />
                    <div>No blocked dates</div>
                    <div style={{ fontSize: 12 }}>All dates are available for booking</div>
                  </div>
                ) : (
                  <List
                    dataSource={blockedDates}
                    renderItem={(block) => (
                      <List.Item
                        actions={[
                          <Button
                            key="delete"
                            type="text"
                            danger
                            icon={<FontAwesomeIcon icon={faTrash} />}
                            onClick={() => handleUnblockDate(block.id)}
                        />
                      ]}
                    >
                      <List.Item.Meta
                        title={
                          <Space direction="vertical" size={4}>
                            <Text strong>
                              {moment.utc(block.startDate).format('MMM DD, YYYY')}
                              {block.startDate !== block.endDate && (
                                <> - {moment.utc(block.endDate).format('MMM DD, YYYY')}</>
                              )}
                            </Text>
                            <div>
                              {block.appliesTo === 'all' ? (
                                <Tag color="orange" style={{ marginRight: 8 }}>
                                  Global
                                </Tag>
                              ) : block.eventTypes && block.eventTypes.length > 0 && (
                                <Tag color="blue" style={{ marginRight: 8 }}>
                                  {block.eventTypes[0].name}
                                </Tag>
                              )}
                            </div>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              {block.reason}
                            </Text>
                          </Space>
                        }
                      />
                    </List.Item>
                  )}
                />
              )}
            </BlockedDatesList>
          </Card>
          </AnimatedSidePanel>
        </Col>
      </Row>

      <Modal
        title="Block Dates from Booking"
        open={modalVisible}
        onOk={handleBlockDates}
        onCancel={() => {
          setModalVisible(false);
          setSelectedDates(null);
          setReason('');
        }}
        okText="Block Dates"
        width={500}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <div>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
              Select Date Range
            </label>
            <RangePicker
              style={{ width: '100%', height: '40px' }}
              value={selectedDates}
              onChange={setSelectedDates}
              format="YYYY-MM-DD"
              disabledDate={(current) => current && current < moment().startOf('day')}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
              Block Type
            </label>
            <Select
              style={{ width: '100%', height: '40px' }}
              value={blockType}
              onChange={setBlockType}
            >
              <Option value="full-day">Full Day</Option>
              <Option value="custom">Custom Hours (Coming Soon)</Option>
            </Select>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
              Reason (Optional)
            </label>
            <Input.TextArea
              style={{ minHeight: '40px' }}
              rows={3}
              placeholder="e.g., Holiday, Office Closed, Staff Training"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>

          <div style={{ 
            padding: 12, 
            background: '#fff7e6', 
            border: '1px solid #ffd591', 
            borderRadius: 8 
          }}>
            <Text style={{ fontSize: 12, color: '#ad6800' }}>
              <strong>Note:</strong> Blocked dates will immediately prevent new bookings from being made on the public calendar.
            </Text>
          </div>
        </Space>
      </Modal>
    </TabContentWrapper>
  );
};

export default AvailabilityManager;
